import { useMutation } from "@apollo/client";
import type { AppointmentDetailFragment } from "@/gql/graphql";
import { APPOINTMENT_QUERY, UPDATE_APPOINTMENT_SERVICES_MUTATION } from "@/graphql/appointments";
import { handleGraphQLErrors } from "@/lib/error/apollo-utils";
import { useAsyncAction } from "@/lib/hooks/use-async-action";
import { useSalonStore } from "@/lib/store/salon";

interface UpdateServicesInput {
  services: Array<{
    id?: string;
    serviceId: string;
    workerId: string | null;
    selectedOptionIds: string[];
  }>;
  forceOverride?: boolean;
}

export interface UseUpdateAppointmentServicesReturn {
  updateServices: (
    appointmentId: string,
    input: UpdateServicesInput
  ) => Promise<AppointmentDetailFragment | null>;
  isUpdating: boolean;
  error: Error | null;
  reset: () => void;
}

export const useUpdateAppointmentServices = (): UseUpdateAppointmentServicesReturn => {
  const currentSalon = useSalonStore((state) => state.currentSalon);

  const [updateAppointmentServicesMutation] = useMutation(UPDATE_APPOINTMENT_SERVICES_MUTATION, {
    errorPolicy: "all",
  });

  const { execute, isLoading, error, reset } = useAsyncAction(
    async (appointmentId: string, input: UpdateServicesInput) => {
      if (!currentSalon?.id) {
        throw new Error("No salon selected");
      }

      const result = await updateAppointmentServicesMutation({
        variables: {
          salonId: currentSalon.id,
          appointmentId,
          input,
        },
        refetchQueries: [
          {
            query: APPOINTMENT_QUERY,
            variables: {
              salonId: currentSalon.id,
              id: appointmentId,
            },
          },
        ],
        awaitRefetchQueries: true,
      });

      if (result.errors) {
        handleGraphQLErrors(result.errors);
      }

      return result.data?.updateAppointmentServices ?? null;
    },
    {
      loadingMessage: "Updating services...",
      successMessage: "Services updated successfully",
      errorMessage: "Failed to update services",
    }
  );

  return {
    updateServices: execute,
    isUpdating: isLoading,
    error,
    reset,
  };
};
